import { useEffect, useRef, useState } from 'react';
import { useSignUpMutation } from 'api/authApi';
import { REFERRAL_CODE } from 'constants/config';
import { REQUIRED_ERROR, SIGN_IN_PAGE_INVESTMENT_TITLE, SIGN_IN_PAGE_TITLE } from 'constants/index';
import mixpanelEvents from 'constants/mixpanel';
import { DIGIT_REGEX } from 'constants/regex';
import { Alert } from 'destiny/dist/components/molecules/alert';
import SvgSpriteLoader from 'destiny/dist/components/molecules/SvgSpriteLoader';
import { ICON_SPRITE_TYPES } from 'destiny/dist/constants/icons';
import { AlertTypes } from 'destiny/dist/constants/molecules/alert';
import { useFormik } from 'formik';
import { useCapabilityIntent } from 'hooks/useCapabilityIntent';
import { useQueryValue } from 'hooks/useQueryValue';
import LoginLayout from 'layout/login-layout';
import { EMPTY_TOAST, SIGNUP_STEPS } from 'modules/auth/auth.constants';
import { SignUpInfoType } from 'modules/auth/auth.types';
import SignUpDetailsForm from 'modules/auth/signup/SignUpDetailsForm';
import SignUpDisclaimer from 'modules/auth/signup/SignUpDisclaimer';
import SignUpMultiEntityForm from 'modules/auth/signup/SignUpMultiEntityForm';
import SignUpOtpForm from 'modules/auth/signup/SignUpOtpForm';
import { isZampInvestDashboard } from 'utils/common';
import { trackMixpanel } from 'utils/mixpanel';
import * as yup from 'yup';

const Signup = () => {
  const referralCode = useQueryValue(REFERRAL_CODE);

  const [currentStep, setCurrentStep] = useState<SIGNUP_STEPS>(SIGNUP_STEPS.USER_DETAILS);
  const [signUpInfo, setSignUpInfo] = useState<SignUpInfoType>({
    email: '',
    firstName: '',
    lastName: '',
    token: '',
    merchantName: '',
    phone: '',
    entityDetails: [{ name: '', geography: '', country: '' }],
    referralCode: '',
  });
  const [toast, setToast] = useState(EMPTY_TOAST);
  const isZampInvest = isZampInvestDashboard();

  const { currentCapability } = useCapabilityIntent();
  const [signup, { isLoading: isSignUpLoading }] = useSignUpMutation();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    trackMixpanel(mixpanelEvents.SIGNUP_PAGE_LOAD, {});
  }, []);

  const setSignUpDetails = (values: any) => {
    setSignUpInfo((prev) => ({ ...prev, ...values }));
    setCurrentStep(SIGNUP_STEPS.ENTITY_DETIALS);
    setToast(EMPTY_TOAST);
  };

  const validationSchema = yup.object().shape({
    email: yup.string().email('Please enter a valid email').required(REQUIRED_ERROR),
    firstName: yup.string().required('Please enter your first name').required(REQUIRED_ERROR),
    lastName: yup.string().required('Please enter your last name').required(REQUIRED_ERROR),
    phone: yup
      .string()
      .min(5, 'Please enter a valid phone number')
      .matches(DIGIT_REGEX, 'Please enter a valid phone number')
      .required('Please enter a valid phone number')
      .required(REQUIRED_ERROR),
    phoneCode: yup.string().required(REQUIRED_ERROR),
    referralCode: yup.string(),
  });

  const signUpFormik = useFormik({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      token: '',
      merchantName: '',
      phone: '',
      phoneCode: '',
      countryCode: '',
      referralCode: referralCode as string,
    },
    validationSchema: validationSchema,
    onSubmit: setSignUpDetails,
  });

  const attemptSignup = async () => {
    trackMixpanel(mixpanelEvents.SIGNUP_OTP_REQUEST, signUpInfo);

    try {
      const result = await signup({
        email: signUpInfo.email,
        first_name: signUpInfo?.firstName,
        last_name: signUpInfo?.lastName,
        merchant_name: signUpInfo.entityDetails[0].name,
        mobile_number: `${signUpInfo.phoneRegion?.value ?? ''}${signUpInfo.phone}`,
        capability_intent: [currentCapability],
        referral_code: signUpInfo?.referralCode,
        entity_details: signUpInfo.entityDetails,
      }).unwrap();

      setSignUpInfo({
        ...signUpInfo,
        token: result?.token,
      });
      setCurrentStep(SIGNUP_STEPS.OTP);
      setToast(EMPTY_TOAST);
    } catch (e: any) {
      setToast({
        hidden: false,
        isError: true,
        message: e?.data?.error?.message || 'Some error occured',
      });
    }
  };

  const scrollToBottom = () => {
    scrollContainerRef?.current?.scrollTo({
      top: scrollContainerRef?.current?.scrollHeight,
      left: 0,
      behavior: 'smooth',
    });
  };

  return (
    <LoginLayout isTransition={currentStep === SIGNUP_STEPS.OTP}>
      <div className='tw-h-screen'>
        <div
          ref={scrollContainerRef}
          className={`tw-no-scrollbar tw-overflow-x-hidden ${
            currentStep === SIGNUP_STEPS.OTP ? 'tw-flex tw-flex-col tw-justify-center' : 'tw-overflow-y-auto'
          } tw-h-full tw-pt-14 tw-pb-40 tw-w-full`}
        >
          <div data-testid='signup-title' className='f-24-500 tw-text-TEXT_PRIMARY'>
            {isZampInvest ? SIGN_IN_PAGE_INVESTMENT_TITLE : SIGN_IN_PAGE_TITLE}
          </div>
          <div data-testid='signup-subtitle' className='f-16-300 tw-text-TEXT_PRIMARY tw-mt-4'>
            {isZampInvest ? '' : 'Get started in 10 mins'}
          </div>
          {!toast.hidden && (
            <Alert
              className='tw-self-stretch f-12-300 !tw-justify-start tw-mt-9 tw-w-full'
              alertType={toast?.isError ? AlertTypes.ERROR : AlertTypes.PENDING}
              id='SIGNUP_TOAST'
            >
              {toast.isError && (
                <SvgSpriteLoader
                  id='alert-triangle'
                  iconCategory={ICON_SPRITE_TYPES.ALERTS_AND_FEEDBACK}
                  width={12}
                  height={12}
                  className='tw-mr-2'
                />
              )}

              {toast.message}
            </Alert>
          )}
          {currentStep === SIGNUP_STEPS.USER_DETAILS && (
            <SignUpDetailsForm setSignUpInfo={setSignUpInfo} signUpInfo={signUpInfo} signUpFormik={signUpFormik} />
          )}
          {currentStep === SIGNUP_STEPS.ENTITY_DETIALS && (
            <SignUpMultiEntityForm
              setSignUpInfo={setSignUpInfo}
              signUpInfo={signUpInfo}
              setCurrentStep={setCurrentStep}
              setToast={setToast}
              isLoading={isSignUpLoading}
              attemptSignup={attemptSignup}
              scrollToBottom={scrollToBottom}
            />
          )}
          {currentStep === SIGNUP_STEPS.OTP && (
            <SignUpOtpForm
              setCurrentStep={setCurrentStep}
              signUpInfo={signUpInfo}
              setToast={setToast}
              currentCapability={currentCapability}
            />
          )}
        </div>
      </div>
      <SignUpDisclaimer />
    </LoginLayout>
  );
};

export default Signup;
